import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { cloneDeep, sortBy } from 'lodash';
import AutoComplete from '@material-ui/lab/Autocomplete';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import * as BobActions from '../../../actions/bobActions';
import * as DashboardActions from '../../../actions/dashboardActions';
import * as K2ACoreActions from '../../../actions/k2aCoreActions';
import * as ReportingActions from '../../../actions/reportingActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import BobConfirmButton from '../../../components/BobConfirmButton';
import BobCancelButton from '../../../components/BobCancelButton';
import { useFormik } from 'formik';
import * as Yup from 'yup';


import classes from './BillingMetricsReport.module.css';

const ValidationSchema = Yup.object().shape({
    company: Yup.string().required(),
    client:  Yup.string().nullable()
});

const BillingMetricsReport = ({
    bobLoading,
    companies,
    createBillingMetricsReport,
    fetchClients,
    handleBackClick
}) => {
    const [companyList, setCompanyList] = useState([]);
    const [clientList, setClientList] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        setCompaniesLoading(true);
        let tempCompanyList = [];
        if (companies.length > 0) {
            const clonedCompanies = cloneDeep(companies);
            tempCompanyList = clonedCompanies.sort(function (a, b) {
                if (a.company.name < b.company.name) {
                    return -1;
                }
                else if (a.company.name > b.company.name) {
                    return 1;
                }
                else {
                    return 0;
                }
            });
            setCompanyList(tempCompanyList.map(c => c.company));
        }
        setCompaniesLoading(false);
    }, [companies]);
    
    const formik = useFormik({
        initialValues: {
            company: null,
            client:  null
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const company = values.company.id;
            const client = values.client ? values.client.id : null;
            await createBillingMetricsReport(company, client);
            handleBackClick();
        }
    });

    const handleCompanyChange = (props, value) => {
        formik.setFieldValue('company', value);
        loadClientList(value.id);
    };

    const loadClientList = async companyId => {
        setLoading(true);
        const clients = await fetchClients(companyId, {pageSize: 500});
        setClientList(clients);

        setLoading(false);
    };

    const handleClientChange = (props, value) => {
        formik.setFieldValue('client', value);
    };

    return (
        <>
            <style>
                {
                    `.k-splitter-flex .k-pane-static {
                        flex-grow: 0;
                        flex-shrink: 0;
                        flex-basis: fit-content;
                    }
                    `
                }
            </style>
            <div className={classes.Main}>
                <div style={{width: '300px'}}>
                    <FormControl className={Boolean(formik.errors.company) && Boolean(formik.touched.company) ? classes.ErrorWrapper : classes.InputWrapper}>
                        <AutoComplete
                            id="company"
                            name="company"
                            options={sortBy(companyList, 'name')}
                            getOptionLabel={(option) => option.name || ''}
                            getOptionSelected={(option, value) => option && value ? option.name === value.name : ''}
                            value={formik.values.company}
                            onChange={handleCompanyChange}
                            onBlur={formik.handleBlur}
                            size="small"
                            required
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    className={classes.Required}
                                    label="Company"
                                    variant="outlined"
                                    required={true}
                                    error={Boolean(formik.errors.company) && Boolean(formik.touched.company)}
                                    helperText={Boolean(formik.touched.company) && formik.errors.company}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <React.Fragment>
                                                {companiesLoading ? <CircularProgress color="inherit" size={15} /> : null}
                                                {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        ),
                                    }}
                                />}
                        />
                    </FormControl>
                </div>
                <div className={classes.FormPane}>
                    <FormControl className={Boolean(formik.errors.client) && Boolean(formik.touched.client) ? classes.ErrorWrapper : classes.InputWrapper}>
                        <AutoComplete
                            id="client"
                            name="client"
                            options={sortBy(clientList, 'name')}
                            getOptionLabel={(option) => option.name || ''}
                            getOptionSelected={(option, value) => option && value ? option.name === value.name : ''}
                            value={formik.values.client}
                            onChange={handleClientChange}
                            onBlur={formik.handleBlur}
                            size="small"
                            required
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    className={classes.Required}
                                    label="Client"
                                    variant="outlined"
                                    required={false}
                                    error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                                    helperText={Boolean(formik.touched.client) && formik.errors.client}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <React.Fragment>
                                                {loading ? <CircularProgress color="inherit" size={15} /> : null}
                                                {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        ),
                                    }}
                                />}
                        />
                    </FormControl>
                </div>
                <div className={classes.ButtonPane}>
                    <BobConfirmButton
                        className={classes.Button}
                        disabled={formik.values.company === null || bobLoading.includes('fetchBillingMetricReport')}
                        onClick={formik.handleSubmit}
                        label={'Create'}
                        iconSize={15}
                    />
                    <BobCancelButton
                        className={classes.CancelButton}
                        onClick={handleBackClick}
                        disabled={loading}
                        label={'Cancel'}
                    />            
                </div>
            </div>
        </>
    );    
};

const mapStateToProps = state => {
    return ({
        bobLoading: state.bob.bobLoading,
        companies:  state.core.companies
    });
};

const mapDispatchToProps = ({
    ...BobActions,
    ...DashboardActions,
    ...K2ACoreActions,
    ...ReportingActions
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(BillingMetricsReport));
